// src/hooks/useListCalculations.js
import { useCallback } from 'react';
import { isItemInList as checkItemInList } from '../utils/itemUtils';

export const useListCalculations = () => {
  const isItemInList = useCallback((item) => {
    return checkItemInList(item);
  }, []);

  const isItemInListById = useCallback((item, listId) => {
    return item.list_id === listId;
  }, []);

  const filterItemsByList = useCallback((items, listId) => {
    if (!listId) return items;
    return items.filter(item => item.list_id === listId);
  }, []);

  // Calcular el total de todos los items en la lista (price × quantity)
  const calculateListTotal = useCallback((items) => {
    return items.reduce((total, item) => {
      if (isItemInList(item)) {
        const price = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        return total + (price * quantity);
      }
      return total;
    }, 0);
  }, [isItemInList]);

  // Calcular el total solo de los items chequeados en la lista
  const calculateCheckedTotal = useCallback((items) => {
    return items.reduce((total, item) => {
      if (isItemInList(item) && parseInt(item.status) === 1) {
        const price = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        return total + (price * quantity);
      }
      return total;
    }, 0);
  }, [isItemInList]);

  // Calcular el total solo de los items NO chequeados en la lista
  const calculateUncheckedTotal = useCallback((items) => {
    return items.reduce((total, item) => {
      if (isItemInList(item) && parseInt(item.status) === 0) {
        const price = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        return total + (price * quantity);
      }
      return total;
    }, 0);
  }, [isItemInList]);

  // Calcular impuestos
  const calculateTaxes = useCallback((amount, taxRate = 6.5) => {
    return (amount * taxRate) / 100;
  }, []);

  // Calcular total con impuestos
  const calculateTotalWithTaxes = useCallback((amount, taxRate = 6.5) => {
    return amount * (1 + taxRate / 100);
  }, []);

  // Calcular diferencia contra presupuesto
  const calculateVsBudget = useCallback((amount, budget) => {
    return budget - amount; // CORREGIDO: budget - amount (positivo = sobra, negativo = falta)
  }, []);

  // ========== NUEVAS FUNCIONES PARA MÉTRICAS ==========
  
  // Obtener todas las métricas de una lista
  const getAllMetrics = useCallback((items, taxRate, budget) => {
    const inListTotal = calculateListTotal(items);
    const checkedTotal = calculateCheckedTotal(items);
    const uncheckedTotal = calculateUncheckedTotal(items);
    
    const inListPlusTax = calculateTotalWithTaxes(inListTotal, taxRate);
    const checkedPlusTax = calculateTotalWithTaxes(checkedTotal, taxRate);
    const uncheckedPlusTax = calculateTotalWithTaxes(uncheckedTotal, taxRate);
    
    return {
      // Base totals
      inList: inListTotal,
      checked: checkedTotal,
      unchecked: uncheckedTotal,
      
      // With taxes
      inListPlusTax,
      checkedPlusTax,
      uncheckedPlusTax,
      
      // Vs Budget (budget - (metric + tax))
      inListVsBudget: calculateVsBudget(inListPlusTax, budget),
      checkedVsBudget: calculateVsBudget(checkedPlusTax, budget),
      uncheckedVsBudget: calculateVsBudget(uncheckedPlusTax, budget)
    };
  }, [calculateListTotal, calculateCheckedTotal, calculateUncheckedTotal, calculateTotalWithTaxes, calculateVsBudget]);

  // Obtener métrica específica por ID
  const getMetricValue = useCallback((items, metricId, taxRate, budget) => {
    const metrics = getAllMetrics(items, taxRate, budget);
    return metrics[metricId];
  }, [getAllMetrics]);

  // Formatear métrica para display
  const formatMetricValue = useCallback((value, isNegativeType = false) => {
    const absValue = Math.abs(value).toFixed(2);
    const sign = isNegativeType && value < 0 ? '-' : '';
    return `${sign}$${absValue}`;
  }, []);

  // ========== FUNCIONES EXISTENTES ==========

  // Función para ordenar items del catálogo por fecha de compra
  const sortByDate = useCallback((items, order = 'recent') => {
    return [...items].sort((a, b) => {
      const dateA = a.date && a.date !== '0000-00-00' ? new Date(a.date) : null;
      const dateB = b.date && b.date !== '0000-00-00' ? new Date(b.date) : null;
      
      // Ambos sin fecha
      if (!dateA && !dateB) return 0;
      // Solo A sin fecha -> A va después
      if (!dateA) return 1;
      // Solo B sin fecha -> B va después
      if (!dateB) return -1;
      
      // Ambos tienen fecha
      if (order === 'recent') {
        // Más reciente primero (mayor fecha primero)
        return dateB - dateA;
      } else {
        // Más antiguo primero (menor fecha primero)
        return dateA - dateB;
      }
    });
  }, []);

  // Ordenar items por precio
  const sortByPrice = useCallback((items, order = 'asc') => {
    return [...items].sort((a, b) => {
      const priceA = parseFloat(a.price) || 0;
      const priceB = parseFloat(b.price) || 0;
      
      if (order === 'asc') {
        return priceA - priceB;
      } else {
        return priceB - priceA;
      }
    });
  }, []);

  // Ordenar items según criterio (para List View)
  const sortItems = useCallback((items, sortBy) => {
    const sortedItems = [...items];
    
    switch (sortBy) {
      case 'Checked':
        // No chequeados primero, luego chequeados
        return sortedItems.sort((a, b) => {
          const aChecked = parseInt(a.status) === 1;
          const bChecked = parseInt(b.status) === 1;
          if (aChecked && !bChecked) return 1;
          if (!aChecked && bChecked) return -1;
          return 0;
        });
        
      case 'Alphabet':
        // Orden alfabético por nombre del item
        return sortedItems.sort((a, b) => a.item.localeCompare(b.item));
        
      case 'Low to high price':
        return sortByPrice(sortedItems, 'asc');
        
      case 'High to low price':
        return sortByPrice(sortedItems, 'desc');
        
      default:
        return sortedItems;
    }
  }, [sortByPrice]);

  // Ordenar items del catálogo según criterio
  const sortCatalogItems = useCallback((items, sortBy) => {
    switch (sortBy) {
      case 'Recently purchased':
        return sortByDate(items, 'recent');
        
      case 'Long-ago purchased':
        return sortByDate(items, 'old');
        
      case 'Alphabet':
        return [...items].sort((a, b) => a.item.localeCompare(b.item));
        
      case 'Low to high price':
        return sortByPrice(items, 'asc');
        
      case 'High to low price':
        return sortByPrice(items, 'desc');
        
      default:
        return items;
    }
  }, [sortByDate, sortByPrice]);

  // Filtrar items que están en la lista y aplicar ordenamiento
  const getListItems = useCallback((allItems, sortBy = 'Checked') => {
    const listItems = allItems.filter(isItemInList);
    return sortItems(listItems, sortBy);
  }, [isItemInList, sortItems]);

  const getListItemsByListId = useCallback((allItems, listId, sortBy = 'Checked') => {
    const listItems = allItems.filter(item => 
      isItemInList(item) && item.list_id === listId
    );
    return sortItems(listItems, sortBy);
  }, [isItemInList, sortItems]);

  return {
    // Funciones existentes
    isItemInList,
    isItemInStore: isItemInListById,
    filterItemsByStore: filterItemsByList,
    isItemInListById,
    filterItemsByList,
    calculateListTotal,
    calculateCheckedTotal,
    calculateUncheckedTotal,
    calculateTaxes,
    calculateTotalWithTaxes,
    calculateVsBudget,
    sortItems,
    sortCatalogItems,
    getListItems,
    getStoreListItems: getListItemsByListId,
    getListItemsByListId,
    
    // Nuevas funciones para métricas
    getAllMetrics,
    getMetricValue,
    formatMetricValue
  };
};